import { UserPlus } from 'lucide-react'

import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'

/** Two-letter initials from an email local-part (no names available). */
export function emailInitials(email: string): string {
  const local = email.split('@')[0] ?? email
  const [first, second] = local.split(/[._-]+/).filter(Boolean)
  if (first && second) {
    return (first.charAt(0) + second.charAt(0)).toUpperCase()
  }
  return local.slice(0, 2).toUpperCase() || '?'
}

// Stable per-email color so the same person keeps the same swatch across renders.
const AVATAR_BG = [
  'bg-primary-solid',
  'bg-david-solid',
  'bg-hanna-solid',
  'bg-angelica-solid',
]
function bgForEmail(email: string): string {
  let hash = 0
  for (let i = 0; i < email.length; i++) {
    hash = (hash * 31 + email.charCodeAt(i)) >>> 0
  }
  return AVATAR_BG[hash % AVATAR_BG.length] ?? 'bg-primary-solid'
}

/** Initials avatar for an email (no image; stable color per email). */
export function EmailAvatar({
  email,
  className,
}: {
  email: string
  className?: string
}) {
  return (
    <Avatar className={cn('size-7 border-2 border-app', className)}>
      <AvatarFallback
        className={cn('text-[10px] font-medium text-white', bgForEmail(email))}>
        {emailInitials(email)}
      </AvatarFallback>
    </Avatar>
  )
}

type AccessAvatarsProps = {
  emails: string[]
  /** Opens the sharing editor (the whole cluster is the entry point). */
  onClick: () => void
  /**
   * Names what the cluster opens, for screen readers. Required because the
   * same cluster fronts documents and duties, and "Edit document details" on a
   * duty card would be wrong.
   */
  ariaLabel: string
  max?: number
}

export function AccessAvatars({
  emails,
  onClick,
  ariaLabel,
  max = 3,
}: AccessAvatarsProps) {
  if (emails.length === 0) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        className="flex cursor-pointer items-center gap-1.5 rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <UserPlus className="size-4" />
        Add people
      </button>
    )
  }

  const shown = emails.slice(0, max)
  const overflow = emails.length - shown.length

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className="flex cursor-pointer items-center -space-x-2 rounded-md p-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      {shown.map((email) => (
        <EmailAvatar key={email} email={email} />
      ))}
      {overflow > 0 && (
        <Avatar className="size-7 border-2 border-app">
          <AvatarFallback className="bg-muted text-[10px] font-medium text-muted-foreground">
            +{overflow}
          </AvatarFallback>
        </Avatar>
      )}
    </button>
  )
}
